import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Link,
  InputAdornment,
} from '@mui/material';
import { Email, Lock } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { apiClient, TokenManager } from '../../services/api';
import OTPService from '../../services/otpService';
import ParticleBackground from '../../components/common/ParticleBackground';

const LoginOTPPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);

  // Get email and name from location state
  const { email, name } = location.state || {};

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Send OTP on page load
  useEffect(() => {
    if (email) {
      handleSendOTP();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendOTP = async () => {
    if (!email) {
      setError('Email is missing. Please try logging in again.');
      return;
    }

    try {
      setResending(true);
      setError('');
      setMessage('');

      await OTPService.sendVerificationOTP(email);
      setMessage('OTP sent to your email!');
      setCountdown(120); // 2 minutes cooldown
    } catch (err: any) {
      if (err.response?.status === 429 && err.response?.data?.remainingSeconds) {
        setCountdown(err.response.data.remainingSeconds);
        setError(err.response.data.message);
      } else {
        setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError('Email is missing. Please try logging in again.');
      return;
    }

    if (otp.length !== 6) {
      setError('OTP must be 6 digits');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');

      const response = await apiClient.post('/auth/verify-otp', {
        email,
        otp
      });

      if (response.success) {
        // Login flow - store token and check if account is approved
        const userData = response.data?.user;
        const token = response.data?.token;

        // Store token for automatic login
        if (token) {
          TokenManager.setToken(token);
          // Store user data with all required fields
          localStorage.setItem('user_data', JSON.stringify({
            id: userData.id,
            name: userData.name,
            email: userData.email,
            role: userData.role,
            branch: userData.branch,
            collegeId: userData.collegeId,
            collegeName: userData.collegeName,
            emailVerified: true, // Email is now verified
            isVerified: userData.isVerified, // Management approval status
            profilePicture: userData.profilePicture,
            mobileNumber: userData.mobileNumber
          }));
        }

        // Check if management has approved the account (isVerified)
        if (userData && userData.isVerified === true) {
          // Account is approved by management - redirect to clubs
          setMessage('Login successful! Redirecting to your dashboard...');
          setTimeout(() => {
            // Force reload to update auth context
            window.location.href = '/clubs';
          }, 1500);
        } else {
          // Email verified but account not yet approved by management
          // Show student approval page
          setMessage('Email verified! Your account is pending management approval...');
          setTimeout(() => {
            window.location.href = '/pending-approval';
          }, 1500);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to verify OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    await handleSendOTP();
  };

  if (!email) {
    return (
      <>
        <ParticleBackground />
        <Container maxWidth="sm">
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Alert severity="error" sx={{ mb: 3 }}>
                Email address is missing. Please login again.
              </Alert>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </Paper>
          </Box>
        </Container>
      </>
    );
  }

  return (
    <>
      <ParticleBackground />
      <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2,
            width: '100%',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CheckCircleIcon
              sx={{
                fontSize: 64,
                color: 'primary.main',
                mb: 2,
              }}
            />
            <Typography variant="h5" gutterBottom fontWeight="600">
              Welcome Back, {name || 'there'}!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Verify your email to continue
            </Typography>
            <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>Email Verification Required</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                We've sent a 6-digit verification code to
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                <Email fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {email}
                </Typography>
              </Box>
            </Alert>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {message && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}

          <Box component="form" onSubmit={handleVerify}>
            <TextField
              fullWidth
              label="Enter OTP"
              value={otp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setOtp(value);
              }}
              placeholder="000000"
              inputProps={{
                maxLength: 6,
                style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px' }
              }}
              sx={{ mb: 3 }}
              helperText="Enter the 6-digit code sent to your email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || otp.length !== 6}
              sx={{
                mb: 2,
                py: 1.75,
                fontWeight: 600,
                borderRadius: 2.5,
                fontSize: '1.1rem',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Verify & Login'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Didn't receive the code?
              </Typography>
              <Button
                variant="text"
                onClick={handleResendOTP}
                disabled={resending || countdown > 0}
              >
                {resending ? (
                  <CircularProgress size={20} />
                ) : countdown > 0 ? (
                  `Resend OTP (${countdown}s)`
                ) : (
                  'Resend OTP'
                )}
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate('/login')}
                sx={{ textDecoration: 'none' }}
              >
                Back to Login
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
    </>
  );
};

export default LoginOTPPage;
